import { useState, useEffect } from 'react';
import { RichTextEditor } from '../common/RichTextEditor';
import {
  getGenericPages,
  createGenericPage,
  updateGenericPage,
  deleteGenericPage,
} from '../../services/firebase';
import { Button } from '../common/Button';
import { Badge } from '../common/Badge';
import { Modal } from '../common/Modal';
import type { GenericPage } from '../../types';
import { extractYouTubeId, youTubeEmbedUrl } from '../../utils/youtube';
import toast from 'react-hot-toast';

type VideoEntry = { url: string; title: string };

function emptyVideo(): VideoEntry {
  return { url: '', title: '' };
}

export function GenericPages() {
  const [pages, setPages] = useState<GenericPage[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingPage, setEditingPage] = useState<GenericPage | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [editorMode, setEditorMode] = useState<'visual' | 'html'>('visual');

  // Form state
  const [formId, setFormId] = useState('');
  const [formTitle, setFormTitle] = useState('');
  const [formContent, setFormContent] = useState('');
  const [formEnabled, setFormEnabled] = useState(true);
  const [formHeaderVideoUrl, setFormHeaderVideoUrl] = useState('');
  const [formHeaderVideoTitle, setFormHeaderVideoTitle] = useState('');
  const [formVideos, setFormVideos] = useState<VideoEntry[]>([]);

  useEffect(() => {
    loadPages();
  }, []);

  const loadPages = async () => {
    setLoading(true);
    try {
      const result = await getGenericPages();
      setPages(result);
    } catch (error) {
      console.error('Error loading generic pages:', error);
      toast.error('Failed to load pages');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormId('');
    setFormTitle('');
    setFormContent('');
    setFormEnabled(true);
    setFormHeaderVideoUrl('');
    setFormHeaderVideoTitle('');
    setFormVideos([]);
  };

  const openCreate = () => {
    resetForm();
    setIsCreating(true);
    setEditingPage(null);
  };

  const openEdit = (page: GenericPage) => {
    setFormId(page.id);
    setFormTitle(page.title);
    setFormContent(page.content);
    setFormEnabled(page.enabled);
    setFormHeaderVideoUrl(page.headerVideo?.url || '');
    setFormHeaderVideoTitle(page.headerVideo?.title || '');
    setFormVideos(
      (page.videos || []).map((v) => ({ url: v.url, title: v.title || '' })),
    );
    setEditingPage(page);
    setIsCreating(false);
    setEditorMode('visual');
  };

  const closeEditor = () => {
    setEditingPage(null);
    setIsCreating(false);
    setShowPreview(false);
    setEditorMode('visual');
  };

  const handleSave = async () => {
    if (!formTitle.trim()) {
      toast.error('Title is required');
      return;
    }
    if (isCreating && !formId.trim()) {
      toast.error('Document ID is required');
      return;
    }

    // Validate header video
    let headerVideoPayload: { url: string; title?: string } | null = null;
    if (formHeaderVideoUrl.trim()) {
      const videoId = extractYouTubeId(formHeaderVideoUrl.trim());
      if (!videoId) {
        toast.error('Header video must be a valid YouTube URL');
        return;
      }
      headerVideoPayload = { url: youTubeEmbedUrl(videoId) };
      if (formHeaderVideoTitle.trim()) headerVideoPayload.title = formHeaderVideoTitle.trim();
    }

    // Validate additional videos
    const videosPayload: Array<{ url: string; title?: string }> = [];
    for (let i = 0; i < formVideos.length; i++) {
      const v = formVideos[i];
      if (!v.url.trim()) continue; // skip blank rows
      const videoId = extractYouTubeId(v.url.trim());
      if (!videoId) {
        toast.error(`Video ${i + 1}: not a valid YouTube URL`);
        return;
      }
      const entry: { url: string; title?: string } = { url: youTubeEmbedUrl(videoId) };
      if (v.title.trim()) entry.title = v.title.trim();
      videosPayload.push(entry);
    }

    setSaving(true);
    try {
      if (isCreating) {
        await createGenericPage(formId.trim(), {
          title: formTitle.trim(),
          content: formContent,
          enabled: formEnabled,
          ...(headerVideoPayload ? { headerVideo: headerVideoPayload } : {}),
          ...(videosPayload.length > 0 ? { videos: videosPayload } : {}),
        });
        toast.success('Page created');
      } else if (editingPage) {
        await updateGenericPage(editingPage.id, {
          title: formTitle.trim(),
          content: formContent,
          enabled: formEnabled,
          headerVideo: headerVideoPayload,   // null clears it via deleteField()
          videos: videosPayload,
        });
        toast.success('Page updated');
      }
      closeEditor();
      loadPages();
    } catch (error) {
      console.error('Error saving page:', error);
      toast.error('Failed to save page');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteGenericPage(id);
      toast.success('Page deleted');
      setDeleteConfirm(null);
      loadPages();
    } catch (error) {
      console.error('Error deleting page:', error);
      toast.error('Failed to delete page');
    }
  };

  const handleToggleEnabled = async (page: GenericPage) => {
    try {
      await updateGenericPage(page.id, { enabled: !page.enabled });
      toast.success(page.enabled ? 'Page disabled' : 'Page enabled');
      loadPages();
    } catch (error) {
      console.error('Error toggling page:', error);
      toast.error('Failed to update page');
    }
  };

  // ── Additional videos helpers ─────────────────────────────────────────────

  const addVideo = () => setFormVideos((prev) => [...prev, emptyVideo()]);

  const removeVideo = (index: number) =>
    setFormVideos((prev) => prev.filter((_, i) => i !== index));

  const updateVideo = (index: number, field: keyof VideoEntry, value: string) =>
    setFormVideos((prev) =>
      prev.map((v, i) => (i === index ? { ...v, [field]: value } : v)),
    );

  // ── Preview HTML ──────────────────────────────────────────────────────────

  const previewHtml = `
    <!DOCTYPE html>
    <html>
      <head>
        <style>
          body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            line-height: 1.6;
            color: #1a1a1a;
            padding: 20px;
            margin: 0;
            background-color: #ffffff;
          }
          h1 { color: #1a1a1a; font-size: 28px; margin-bottom: 20px; border-bottom: 2px solid #e5e5e5; padding-bottom: 10px; }
          h2 { color: #1a1a1a; font-size: 22px; margin-top: 30px; margin-bottom: 15px; }
          h3 { color: #1a1a1a; font-size: 18px; margin-top: 25px; margin-bottom: 10px; }
          p { margin-bottom: 15px; text-align: justify; }
          ul, ol { margin-bottom: 15px; padding-left: 20px; }
          li { margin-bottom: 8px; }
          strong { color: #1a1a1a; }
          a { color: #0066cc; text-decoration: none; }
          a:hover { text-decoration: underline; }
          .last-updated { font-style: italic; color: #666666; font-size: 14px; }
          .video-container { margin-bottom: 24px; }
          .video-title { font-weight: 600; margin-bottom: 8px; }
          .aspect-video { position: relative; padding-bottom: 56.25%; height: 0; overflow: hidden; }
          .aspect-video iframe { position: absolute; top: 0; left: 0; width: 100%; height: 100%; border: 0; }
        </style>
      </head>
      <body>
        ${formHeaderVideoUrl.trim() && extractYouTubeId(formHeaderVideoUrl.trim()) ? `
          <div class="video-container">
            ${formHeaderVideoTitle.trim() ? `<p class="video-title">${formHeaderVideoTitle.trim()}</p>` : ''}
            <div class="aspect-video">
              <iframe src="${youTubeEmbedUrl(extractYouTubeId(formHeaderVideoUrl.trim())!)}"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowfullscreen></iframe>
            </div>
          </div>
        ` : ''}
        ${formContent}
        ${formVideos.filter(v => v.url.trim() && extractYouTubeId(v.url.trim())).map(v => `
          <div class="video-container">
            ${v.title.trim() ? `<p class="video-title">${v.title.trim()}</p>` : ''}
            <div class="aspect-video">
              <iframe src="${youTubeEmbedUrl(extractYouTubeId(v.url.trim())!)}"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowfullscreen></iframe>
            </div>
          </div>
        `).join('')}
        <div class="last-updated">
          <p><strong>Last updated:</strong> ${new Date().toLocaleDateString()}</p>
        </div>
      </body>
    </html>
  `;

  // ── YouTube input sub-component (inline) ─────────────────────────────────

  function YouTubeInput({
    label,
    url,
    onUrlChange,
    titleValue,
    onTitleChange,
    titlePlaceholder = 'Optional title',
  }: {
    label: string;
    url: string;
    onUrlChange: (v: string) => void;
    titleValue: string;
    onTitleChange: (v: string) => void;
    titlePlaceholder?: string;
  }) {
    const videoId = url.trim() ? extractYouTubeId(url.trim()) : null;
    const isInvalid = url.trim() && !videoId;

    return (
      <div className="space-y-2">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
          <input
            type="url"
            value={url}
            onChange={(e) => onUrlChange(e.target.value)}
            placeholder="https://www.youtube.com/watch?v=..."
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
          />
          {isInvalid && (
            <p className="mt-1 text-xs text-red-600">
              Not a recognised YouTube URL. Supported: youtube.com/watch, youtu.be, youtube.com/embed, youtube.com/shorts.
            </p>
          )}
          {videoId && (
            <p className="mt-1 text-xs text-green-600">Valid YouTube video (ID: {videoId})</p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Title <span className="font-normal text-gray-400">(optional)</span></label>
          <input
            type="text"
            value={titleValue}
            onChange={(e) => onTitleChange(e.target.value)}
            placeholder={titlePlaceholder}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
          />
        </div>
        {videoId && (
          <div className="aspect-video w-full max-w-sm rounded overflow-hidden bg-black mt-2">
            <iframe
              src={youTubeEmbedUrl(videoId)}
              title="YouTube preview"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="w-full h-full"
            />
          </div>
        )}
      </div>
    );
  }

  // ── Editor view ───────────────────────────────────────────────────────────

  if (isCreating || editingPage) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={closeEditor} className="text-gray-500 hover:text-gray-700">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <h2 className="text-xl font-semibold text-gray-900">
              {isCreating ? 'Create Page' : `Edit: ${editingPage?.title}`}
            </h2>
          </div>
          <div className="flex gap-2">
            {!showPreview && (
              <div className="flex border border-gray-300 rounded-lg overflow-hidden">
                <button
                  onClick={() => setEditorMode('visual')}
                  className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                    editorMode === 'visual' ? 'bg-brand-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Visual
                </button>
                <button
                  onClick={() => setEditorMode('html')}
                  className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                    editorMode === 'html' ? 'bg-brand-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  HTML
                </button>
              </div>
            )}
            <Button variant="secondary" onClick={() => setShowPreview(!showPreview)}>
              {showPreview ? 'Edit' : 'Preview'}
            </Button>
            <Button onClick={handleSave} loading={saving}>
              {isCreating ? 'Create' : 'Save Changes'}
            </Button>
          </div>
        </div>

        {showPreview ? (
          /* Preview Mode */
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="border-b px-4 py-3 bg-gray-50">
              <h3 className="text-sm font-medium text-gray-700">Mobile App Preview</h3>
            </div>
            <div className="max-w-2xl mx-auto">
              <iframe
                title="Page Preview"
                srcDoc={previewHtml}
                className="w-full border-0"
                style={{ height: '600px' }}
              />
            </div>
          </div>
        ) : (
          /* Edit Mode */
          <div className="space-y-4">
            {/* Document ID (only for new pages) */}
            {isCreating && (
              <div className="bg-white shadow rounded-lg p-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Document ID</label>
                <input
                  type="text"
                  value={formId}
                  onChange={(e) =>
                    setFormId(
                      e.target.value
                        .toLowerCase()
                        .replace(/\s+/g, '_')
                        .replace(/[^a-z0-9_]/g, ''),
                    )
                  }
                  placeholder="e.g., privacy_policy, terms_of_service"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Lowercase letters, numbers, and underscores only. This cannot be changed later.
                </p>
              </div>
            )}

            {/* Title and Enabled */}
            <div className="bg-white shadow rounded-lg p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                  <input
                    type="text"
                    value={formTitle}
                    onChange={(e) => setFormTitle(e.target.value)}
                    placeholder="Page title"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  />
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formEnabled}
                      onChange={(e) => setFormEnabled(e.target.checked)}
                      className="rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                    />
                    <span className="text-sm text-gray-700">Enabled (visible in app)</span>
                  </label>
                </div>
              </div>
            </div>

            {/* Header Video */}
            <div className="bg-white shadow rounded-lg p-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Header Video</h3>
              <p className="text-xs text-gray-500 mb-3">
                Optional hero video shown at the top of the page, before the content.
              </p>
              <YouTubeInput
                label="YouTube URL"
                url={formHeaderVideoUrl}
                onUrlChange={setFormHeaderVideoUrl}
                titleValue={formHeaderVideoTitle}
                onTitleChange={setFormHeaderVideoTitle}
                titlePlaceholder="e.g. Watch Our Story"
              />
            </div>

            {/* Content Editor */}
            <div className="bg-white shadow rounded-lg p-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Content {editorMode === 'html' && <span className="text-gray-400 font-normal">(HTML Source)</span>}
              </label>
              {editorMode === 'visual' ? (
                <RichTextEditor
                  value={formContent}
                  onChange={setFormContent}
                  minHeight={400}
                />
              ) : (
                <textarea
                  value={formContent}
                  onChange={(e) => setFormContent(e.target.value)}
                  className="w-full h-[500px] font-mono text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  placeholder="<h1>Page Title</h1>\n<p>Your content here...</p>"
                  spellCheck={false}
                />
              )}
            </div>

            {/* Additional Videos */}
            <div className="bg-white shadow rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="text-sm font-semibold text-gray-700">Additional Videos</h3>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Extra videos shown below the content, in order.
                  </p>
                </div>
                <Button variant="secondary" onClick={addVideo}>
                  Add Video
                </Button>
              </div>

              {formVideos.length === 0 ? (
                <p className="text-sm text-gray-400 italic">No additional videos.</p>
              ) : (
                <div className="space-y-6">
                  {formVideos.map((video, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-4 relative">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                          Video {index + 1}
                        </span>
                        <button
                          onClick={() => removeVideo(index)}
                          className="text-red-500 hover:text-red-700 text-xs font-medium"
                        >
                          Remove
                        </button>
                      </div>
                      <YouTubeInput
                        label="YouTube URL"
                        url={video.url}
                        onUrlChange={(v) => updateVideo(index, 'url', v)}
                        titleValue={video.title}
                        onTitleChange={(v) => updateVideo(index, 'title', v)}
                        titlePlaceholder="e.g. Behind the Scenes"
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── List view ─────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-gray-900">Generic Pages</h2>
        <Button onClick={openCreate}>Create Page</Button>
      </div>

      {pages.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          No pages found. Create your first page to get started.
        </div>
      ) : (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Updated</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {pages.map((page) => (
                <tr key={page.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-600">{page.id}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    <div>{page.title}</div>
                    {(page.headerVideo || (page.videos && page.videos.length > 0)) && (
                      <div className="flex gap-2 mt-1">
                        {page.headerVideo && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700">
                            Header video
                          </span>
                        )}
                        {page.videos && page.videos.length > 0 && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
                            {page.videos.length} video{page.videos.length !== 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <Badge variant={page.enabled ? 'success' : 'warning'}>
                      {page.enabled ? 'Enabled' : 'Disabled'}
                    </Badge>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {page.lastUpdated?.toDate ? page.lastUpdated.toDate().toLocaleDateString() : '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm space-x-2">
                    <button
                      onClick={() => openEdit(page)}
                      className="text-brand-600 hover:text-brand-800 font-medium"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleToggleEnabled(page)}
                      className="text-gray-600 hover:text-gray-800 font-medium"
                    >
                      {page.enabled ? 'Disable' : 'Enable'}
                    </button>
                    <button
                      onClick={() => setDeleteConfirm(page.id)}
                      className="text-red-600 hover:text-red-800 font-medium"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <Modal isOpen onClose={() => setDeleteConfirm(null)} title="Confirm Delete" size="sm">
          <div className="space-y-4">
            <p className="text-gray-700">
              Are you sure you want to delete the page <strong>{deleteConfirm}</strong>? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setDeleteConfirm(null)}>
                Cancel
              </Button>
              <Button variant="danger" onClick={() => handleDelete(deleteConfirm)}>
                Delete
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
